import { ChevronDown, Waypoints } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DEEP_ANALYSIS_LABEL } from '@/hooks/useAnalysisSet';

interface DeepAnalysisHeaderControlProps {
  currentDocuments?: number;
  totalDocuments?: number;
  removingDocuments?: number;
  isLoading?: boolean;
  onClick: () => void;
}

export function DeepAnalysisHeaderControl({
  currentDocuments,
  totalDocuments,
  removingDocuments = 0,
  isLoading = false,
  onClick,
}: DeepAnalysisHeaderControlProps) {
  const hasCount = currentDocuments !== undefined && totalDocuments !== undefined;
  const current = currentDocuments ?? 0;
  const total = totalDocuments ?? 0;
  const isRemoving = removingDocuments > 0;
  const isComplete = total > 0 && current === total && !isRemoving;
  const primaryStatusLabel = isLoading && !hasCount
    ? 'Loading...'
    : total === 0
      ? 'No docs'
      : `${current}/${total} docs`;
  const statusLabel = isRemoving
    ? `${primaryStatusLabel} · ${removingDocuments} removing`
    : primaryStatusLabel;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group inline-flex h-10 cursor-pointer items-center gap-2 rounded-full px-1.5 pr-2.5 text-[13px] text-slate-600 transition-colors hover:bg-slate-50/70 hover:text-slate-800 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-slate-300/60"
      aria-label={`${DEEP_ANALYSIS_LABEL}: ${statusLabel}`}
    >
      <span className="relative top-px flex h-7 w-7 items-center justify-center text-slate-500 transition-colors group-hover:text-slate-700">
        <Waypoints className="h-5 w-5" />
      </span>
      <span className="flex min-w-0 flex-col items-start justify-center gap-1">
        <span className="flex items-center gap-1.5">
          <span className="font-medium leading-none text-slate-700">
            {DEEP_ANALYSIS_LABEL}
          </span>
          <ChevronDown className="relative top-px h-3.5 w-3.5 text-slate-400 transition-colors group-hover:text-slate-600" />
        </span>
        <span
          className={cn(
            'hidden items-center gap-1 text-[10.5px] font-medium leading-none tabular-nums sm:inline-flex',
            !hasCount || (total === 0 && !isRemoving)
              ? 'text-slate-500'
              : isComplete
                ? 'text-teal-800'
                : 'text-amber-800',
          )}
        >
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              !hasCount || (total === 0 && !isRemoving)
                ? 'bg-slate-300'
                : isComplete
                  ? 'bg-teal-600'
                  : 'bg-amber-500',
            )}
          />
          {statusLabel}
        </span>
      </span>
    </button>
  );
}
